import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Play, Trash2, RotateCcw, Bell, CheckCheck, Loader2, Boxes, Tag,
  Layers, AlertTriangle, CheckCircle2, XCircle, Info, Clock, Zap, Check, X, Wifi,
} from "lucide-react";
import AdminShell from "./AdminShell";
import {
  syncStatus, syncQueue, syncLogs, syncNotifications, syncMarkRead, syncMarkAllRead,
  syncRun, syncClearQueue, syncRetryFailed, syncUpdateSettings, syncPriceApprovals,
  syncResolveApproval, cjConnectionStatus, errMessage,
} from "../../lib/api";
import { eur } from "../../config";

const fmt = (v) => (v ? new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const SEV = {
  ERROR: { cls: "text-red-600 bg-red-50 border-red-200", Icon: XCircle },
  CRITICAL: { cls: "text-red-700 bg-red-50 border-red-200", Icon: XCircle },
  WARNING: { cls: "text-amber-600 bg-amber-50 border-amber-200", Icon: AlertTriangle },
  INFO: { cls: "text-green-700 bg-green-50 border-green-200", Icon: CheckCircle2 },
};
const JOB_CLS = {
  COMPLETED: "text-green-700 bg-green-50", WAITING: "text-ardoise bg-rose/20",
  PROCESSING: "text-blue-700 bg-blue-50", FAILED: "text-red-600 bg-red-50",
  RETRYING: "text-amber-600 bg-amber-50", CANCELLED: "text-ardoise/60 bg-gray-100",
};

function Stat({ label, value, Icon, testid }) {
  return (
    <div className="border border-dore/20 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-ardoise/60 text-[11px] uppercase tracking-wider mb-1">
        <Icon size={13} className="text-dore" /> {label}
      </div>
      <div className="text-2xl font-display text-encre">{value}</div>
    </div>
  );
}

function CjConnectionCard() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (force = false) => {
    setBusy(true);
    try { setC(await cjConnectionStatus(force)); } catch (e) { setC({ connected: false, message: errMessage(e, "Test impossible") }); }
    setBusy(false);
  }, []);
  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(false), 60000);
    return () => clearInterval(t);
  }, [load]);

  const fmtT = (v) => (v ? new Date(v).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
  const connected = c?.connected;
  const dot = c == null ? "bg-ardoise/40 animate-pulse" : connected ? "bg-green-500" : "bg-red-500";
  const label = c == null ? "Test en cours…" : connected ? "Connecté" : "Déconnecté";

  return (
    <div className="border border-dore/20 bg-white p-5 mb-5" data-testid="cj-connection-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-encre flex items-center gap-2"><Wifi size={16} className="text-dore" /> Connexion CJdropshipping</h2>
        <button onClick={() => load(true)} disabled={busy} data-testid="cj-test-btn" className="flex items-center gap-1.5 border border-dore/30 px-3 py-1.5 text-[11px] uppercase tracking-wider hover:bg-dore/10 disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Tester maintenant
        </button>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ardoise/50 mb-1">Statut</div>
          <div className="flex items-center gap-2" data-testid="cj-conn-status"><span className={`w-2.5 h-2.5 rounded-full ${dot}`} /> <span className="text-encre">{label}</span></div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ardoise/50 mb-1">Latence</div>
          <div className="text-encre">{c?.latency_ms != null ? `${c.latency_ms} ms` : "—"}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ardoise/50 mb-1">IP dédiée (proxy)</div>
          <div data-testid="cj-proxy-state">
            {c?.proxy_active
              ? <span className="text-green-700 text-xs">✓ Actif {c.proxy_host ? `(${c.proxy_host})` : ""}</span>
              : <span className="text-amber-600 text-xs">Non configuré (IP partagée)</span>}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ardoise/50 mb-1">Dernier test</div>
          <div className="text-ardoise/70">{fmtT(c?.checked_at)}{c?.cached ? " (cache)" : ""}</div>
        </div>
      </div>
      {c && !connected && <p className="text-xs text-red-600 mt-3" data-testid="cj-conn-message">{c.message}</p>}
      {c && connected && c.proxy_active && <p className="text-xs text-green-700 mt-3">✓ Prêt pour la synchro réelle via votre IP dédiée.</p>}
      {c && connected && !c.proxy_active && <p className="text-xs text-amber-600 mt-3">Connecté mais sans IP dédiée — risque de blocage « 3 users/IP ». Configurez <code>CJ_PROXY_URL</code> avant d'activer la synchro réelle.</p>}
    </div>
  );
}

export default function SyncDashboard() {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, q, l, n, a] = await Promise.all([
        syncStatus(), syncQueue({ limit: 10 }), syncLogs({ limit: 15 }),
        syncNotifications({ limit: 20 }), syncPriceApprovals(),
      ]);
      setData(s); setJobs(q.jobs); setLogs(l.logs); setNotifs(n.notifications); setApprovals(a.approvals);
    } catch (e) { toast.error(errMessage(e, "Chargement impossible")); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const run = async (type) => {
    setBusy(type);
    try {
      await syncRun(type);
      toast.success("Synchronisation lancée");
      setTimeout(load, 1500);
    } catch (e) { toast.error(errMessage(e, "Sync impossible")); }
    setBusy("");
  };

  const toggleAuto = async () => {
    setBusy("auto");
    try {
      const next = !data.settings.auto_sync_enabled;
      await syncUpdateSettings({ auto_sync_enabled: next });
      toast.success(next ? "Synchro automatique activée" : "Synchro automatique désactivée");
      load();
    } catch (e) { toast.error(errMessage(e, "Action impossible")); }
    setBusy("");
  };

  const setMode = async (mode) => {
    try {
      await syncUpdateSettings({ cj_mode: mode });
      toast.success(`Mode ${mode === "mock" ? "Simulation" : "Réel"} activé`);
      load();
    } catch (e) { toast.error(errMessage(e, "Action impossible")); }
  };

  const clearQueue = async () => { try { const r = await syncClearQueue(); toast.success(`${r.cancelled} job(s) annulé(s)`); load(); } catch (e) { toast.error(errMessage(e)); } };
  const retryFailed = async () => { try { const r = await syncRetryFailed(); toast.success(`${r.retried} job(s) relancé(s)`); load(); } catch (e) { toast.error(errMessage(e)); } };
  const markAll = async () => { try { await syncMarkAllRead(); load(); } catch (e) { toast.error(errMessage(e)); } };
  const markOne = async (id) => { try { await syncMarkRead(id); load(); } catch { } };
  const resolve = async (id, action) => {
    try { await syncResolveApproval(id, action); toast.success(action === "approve" ? "Prix mis à jour" : "Variation refusée"); load(); }
    catch (e) { toast.error(errMessage(e)); }
  };

  if (!data) return <AdminShell title="Synchronisation"><p className="text-ardoise text-sm">Chargement...</p></AdminShell>;

  const s = data.settings;
  const auto = s.auto_sync_enabled;

  return (
    <AdminShell title="Synchronisation CJ">
      {/* CONNEXION CJ */}
      <CjConnectionCard />

      {/* INDICATEUR EMAILS */}
      <div className="flex items-center gap-2 mb-5 text-sm" data-testid="emails-indicator">
        <span className="text-ardoise/60 uppercase tracking-wider text-[11px]">Emails transactionnels :</span>
        {data.emails?.active
          ? <span className="flex items-center gap-1.5 text-green-700"><CheckCircle2 size={15} /> Actifs via Resend <span className="text-ardoise/50 text-xs">({data.emails.sender})</span></span>
          : <span className="flex items-center gap-1.5 text-amber-600"><AlertTriangle size={15} /> Mode simulation — ajoutez <code className="bg-rose/20 px-1">RESEND_API_KEY</code> pour activer l'envoi réel</span>}
      </div>

      {/* AUTO-SYNC + MODE */}
      <div className="grid lg:grid-cols-2 gap-4 mb-5">
        <div className="border border-dore/20 bg-white p-5" data-testid="autosync-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg text-encre flex items-center gap-2"><Zap size={16} className="text-dore" /> Synchronisation automatique</h2>
            <span className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 ${auto ? "bg-green-50 text-green-700" : "bg-gray-100 text-ardoise"}`} data-testid="autosync-state">
              <span className={`w-2 h-2 rounded-full ${auto ? "bg-green-500" : "bg-red-500"}`} /> {auto ? "ON" : "OFF"}
            </span>
          </div>
          <p className="text-xs text-ardoise/70 mb-4">
            {auto ? "Stocks toutes les 2h · Prix toutes les 6h · Complète toutes les 12h." : "Désactivée. Utilisez la synchro manuelle ci-dessous."}
          </p>
          <button onClick={toggleAuto} disabled={busy === "auto"} data-testid="autosync-toggle"
            className={`w-full py-2.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${auto ? "border border-red-300 text-red-600 hover:bg-red-50" : "bg-noir text-white hover:bg-encre"} disabled:opacity-50 transition-colors`}>
            {busy === "auto" ? <Loader2 size={14} className="animate-spin" /> : null}
            {auto ? "Désactiver l'auto-sync" : "Activer l'auto-sync"}
          </button>
          <div className="mt-4 pt-4 border-t border-dore/10">
            <div className="flex items-center justify-between">
              <span className="text-xs text-ardoise/70 uppercase tracking-wider">Mode fournisseur</span>
              <div className="flex border border-dore/30" data-testid="mode-toggle">
                {["mock", "real"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-wider transition-colors ${s.cj_mode === m ? "bg-dore text-white" : "text-ardoise hover:bg-dore/10"}`}>
                    {m === "mock" ? "Simulation" : "Réel"}
                  </button>
                ))}
              </div>
            </div>
            {s.cj_mode === "mock" && <p className="text-[11px] text-amber-600 mt-2">Mode simulation : données CJ mockées (aucun appel réel).</p>}
          </div>
        </div>

        {/* MANUAL ACTIONS */}
        <div className="border border-dore/20 bg-white p-5" data-testid="manual-card">
          <h2 className="font-display text-lg text-encre mb-3 flex items-center gap-2"><Play size={16} className="text-dore" /> Synchro manuelle</h2>
          <div className="grid sm:grid-cols-3 gap-2 mb-4">
            <button onClick={() => run("STOCK_SYNC")} disabled={busy} data-testid="run-stock" className="flex flex-col items-center gap-1 border border-dore/30 py-3 text-xs hover:bg-dore/10 disabled:opacity-50 transition-colors">
              {busy === "STOCK_SYNC" ? <Loader2 size={16} className="animate-spin" /> : <Boxes size={16} className="text-dore" />} Stocks
            </button>
            <button onClick={() => run("PRICE_SYNC")} disabled={busy} data-testid="run-price" className="flex flex-col items-center gap-1 border border-dore/30 py-3 text-xs hover:bg-dore/10 disabled:opacity-50 transition-colors">
              {busy === "PRICE_SYNC" ? <Loader2 size={16} className="animate-spin" /> : <Tag size={16} className="text-dore" />} Prix
            </button>
            <button onClick={() => run("FULL_SYNC")} disabled={busy} data-testid="run-full" className="flex flex-col items-center gap-1 border border-dore/30 py-3 text-xs hover:bg-dore/10 disabled:opacity-50 transition-colors">
              {busy === "FULL_SYNC" ? <Loader2 size={16} className="animate-spin" /> : <Layers size={16} className="text-dore" />} Complète
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={clearQueue} data-testid="clear-queue" className="flex-1 flex items-center justify-center gap-1.5 border border-dore/30 py-2 text-[11px] uppercase tracking-wider hover:bg-dore/10 transition-colors"><Trash2 size={13} /> Vider la file</button>
            <button onClick={retryFailed} data-testid="retry-failed" className="flex-1 flex items-center justify-center gap-1.5 border border-dore/30 py-2 text-[11px] uppercase tracking-wider hover:bg-dore/10 transition-colors"><RotateCcw size={13} /> Relancer échecs</button>
          </div>
          <p className="text-[11px] text-ardoise/60 mt-3">Cooldown : 1 synchro manuelle toutes les {s.manual_cooldown_minutes} min.</p>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Produits synchro (24h)" value={data.stats_24h.products_synced} Icon={RefreshCw} testid="stat-synced" />
        <Stat label="Erreurs 24h" value={data.stats_24h.errors} Icon={AlertTriangle} testid="stat-errors" />
        <Stat label="Rate-limit 24h" value={data.stats_24h.rate_limit_hits} Icon={Clock} testid="stat-ratelimit" />
        <Stat label="Produits importés" value={data.products_total} Icon={Boxes} testid="stat-products" />
      </div>

      {/* NEXT/LAST SYNC */}
      <div className="border border-dore/20 bg-white p-4 mb-5 grid sm:grid-cols-3 gap-3 text-xs" data-testid="sync-times">
        {[["Stocks", "stock"], ["Prix", "price"], ["Complète", "full"]].map(([label, key]) => (
          <div key={key}>
            <div className="text-ardoise/60 uppercase tracking-wider mb-1">{label}</div>
            <div className="text-encre">Dernière : {fmt(data.last_sync[key])}</div>
            <div className="text-ardoise/70">Prochaine : {auto ? fmt(data.next_sync[key]) : "—"}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* NOTIFICATIONS */}
        <div className="border border-dore/20 bg-white" data-testid="notifs-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-dore/10">
            <h3 className="font-display text-encre flex items-center gap-2"><Bell size={15} className="text-dore" /> Notifications {data.unread_notifications > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{data.unread_notifications}</span>}</h3>
            <button onClick={markAll} data-testid="notif-read-all" className="text-[11px] text-dore hover:text-noir flex items-center gap-1"><CheckCheck size={13} /> Tout lu</button>
          </div>
          <div className="max-h-[360px] overflow-y-auto divide-y divide-dore/10">
            {notifs.length === 0 ? <p className="p-4 text-sm text-ardoise/60" data-testid="notifs-empty">Aucune notification.</p> :
              notifs.map((n) => {
                const cfg = SEV[n.severity] || SEV.INFO; const I = cfg.Icon;
                return (
                  <div key={n.notif_id} className={`p-3 flex gap-3 ${n.read ? "opacity-60" : ""}`} data-testid="notif-item">
                    <I size={16} className={cfg.cls.split(" ")[0] + " mt-0.5 flex-shrink-0"} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-encre truncate">{n.title}</p>
                        <span className="text-[10px] text-ardoise/50 whitespace-nowrap">{fmt(n.created_at)}</span>
                      </div>
                      <p className="text-xs text-ardoise/70 mt-0.5">{n.description}</p>
                      {n.action && <p className="text-[11px] text-dore mt-1">→ {n.action}</p>}
                      <div className="flex items-center gap-2 mt-1.5">
                        {n.email_simulated && <span className="text-[10px] bg-rose/20 text-ardoise px-1.5 py-0.5">📧 Email [Simulé] → {n.email_to}</span>}
                        {!n.read && <button onClick={() => markOne(n.notif_id)} className="text-[10px] text-dore hover:underline">Marquer lu</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* QUEUE + LOGS */}
        <div className="space-y-5">
          <div className="border border-dore/20 bg-white" data-testid="queue-card">
            <div className="px-4 py-3 border-b border-dore/10 flex items-center justify-between">
              <h3 className="font-display text-encre">File d'attente</h3>
              <div className="flex gap-1.5 text-[10px]">
                {Object.entries(data.queue_counts).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} className={`px-1.5 py-0.5 ${JOB_CLS[k] || ""}`}>{k} {v}</span>
                ))}
              </div>
            </div>
            <div className="max-h-[150px] overflow-y-auto divide-y divide-dore/10">
              {jobs.length === 0 ? <p className="p-4 text-sm text-ardoise/60" data-testid="queue-empty">Aucun job.</p> :
                jobs.map((j) => (
                  <div key={j.job_id} className="px-4 py-2 flex items-center justify-between text-xs" data-testid="queue-item">
                    <span className="text-encre">{j.type} <span className="text-ardoise/50">· {j.triggered_by}</span></span>
                    <span className={`px-1.5 py-0.5 ${JOB_CLS[j.status] || ""}`}>{j.status}</span>
                  </div>
                ))}
            </div>
          </div>

          <div className="border border-dore/20 bg-white" data-testid="logs-card">
            <div className="px-4 py-3 border-b border-dore/10"><h3 className="font-display text-encre">Journal (logs)</h3></div>
            <div className="max-h-[220px] overflow-y-auto divide-y divide-dore/10">
              {logs.length === 0 ? <p className="p-4 text-sm text-ardoise/60" data-testid="logs-empty">Aucun log.</p> :
                logs.map((l, i) => {
                  const cfg = SEV[l.severity] || SEV.INFO; const I = cfg.Icon;
                  return (
                    <div key={i} className="px-4 py-2 flex gap-2 text-xs" data-testid="log-item">
                      <I size={14} className={cfg.cls.split(" ")[0] + " mt-0.5 flex-shrink-0"} />
                      <div className="flex-1 min-w-0">
                        <p className="text-encre">{l.message}</p>
                        <span className="text-[10px] text-ardoise/50">{fmt(l.created_at)} · {l.type}</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {/* PRICE APPROVALS */}
      {approvals.length > 0 && (
        <div className="border border-amber-300 bg-amber-50/40 mt-5" data-testid="approvals-card">
          <div className="px-4 py-3 border-b border-amber-200"><h3 className="font-display text-encre flex items-center gap-2"><AlertTriangle size={15} className="text-amber-600" /> Prix à valider (variation &gt; 20%)</h3></div>
          <div className="divide-y divide-amber-100">
            {approvals.map((a) => (
              <div key={a.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="approval-item">
                <div className="text-xs">
                  <p className="text-encre font-medium">{a.product_name} <span className="text-ardoise/50">· {a.sku}</span></p>
                  <p className="text-ardoise/70">Coût {eur(a.old_cost)} → {eur(a.new_cost)} <span className="text-amber-600">({a.variation_pct}%)</span> · Vente suggérée : <b>{eur(a.suggested_selling)}</b></p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => resolve(a.id, "approve")} data-testid="approval-approve" className="flex items-center gap-1 bg-noir text-white px-3 py-1.5 text-[11px] uppercase tracking-wider hover:bg-encre transition-colors"><Check size={13} /> Valider</button>
                  <button onClick={() => resolve(a.id, "reject")} data-testid="approval-reject" className="flex items-center gap-1 border border-ardoise/30 px-3 py-1.5 text-[11px] uppercase tracking-wider hover:bg-white transition-colors"><X size={13} /> Refuser</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
